import { useState } from "react";
import BenefitsTable from "../components/benefits/BenefitsTable";
import FiltersPanel from "../components/benefits/FiltersPanel";
import Pagination from "../components/benefits/Pagination";
import SendModal from "../components/benefits/SendModal";
import Toolbar from "../components/benefits/Toolbar";
import UploadModal from "../components/benefits/UploadModal";
import { AlertIcon } from "../components/ui/Icons";
import { MESSAGE_COST_USD, messagingSpend } from "../config/pricing";
import { useBenefits } from "../hooks/useBenefits";
import { formatUsd } from "../utils/format";

interface IBenefitsPageProps {
  user?: string;
}

interface IStatCard {
  label: string;
  dot: string;
  value: string | null;
  money?: boolean;
  hint?: string;
}

export default function BenefitsPage({ user }: IBenefitsPageProps) {
  const {
    benefits,
    pagination,
    stats,
    isStatsLoading,
    draftValues,
    appliedFilters,
    apiFilters,
    hasActiveFilters,
    limit,
    isLoading,
    error,
    setDraft,
    applyTextFilter,
    applyAnswerFilter,
    removeFilter,
    clearFilters,
    setPage,
    setLimit,
    refresh,
  } = useBenefits();

  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [isSendOpen, setIsSendOpen] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // los totales describen la tabla completa, no solo la pagina visible
  const statCards: IStatCard[] = [
    {
      label: "Total registros",
      dot: "total",
      value: stats ? stats.totalRecords.toLocaleString("es-CL") : null,
    },
    {
      label: "Enviados",
      dot: "sent",
      value: stats ? stats.sentRecords.toLocaleString("es-CL") : null,
    },
    {
      label: "Pendientes",
      dot: "pending",
      value: stats ? stats.pendingRecords.toLocaleString("es-CL") : null,
    },
    {
      label: "Respondidos",
      dot: "answered",
      value: stats ? stats.answeredRecords.toLocaleString("es-CL") : null,
    },
    {
      // el gasto cuenta todo mensaje enviado, aunque el registro ya no esté vigente
      label: "Gasto total",
      dot: "cost",
      value: stats ? formatUsd(messagingSpend(stats.billedRecords)) : null,
      money: true,
      hint: `${formatUsd(MESSAGE_COST_USD, 3)} por mensaje`,
    },
  ];

  const handleLimitChange = (nextLimit: number): void => {
    setLimit(nextLimit);
    setPage(1);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Mensajería Confirmación de Beneficio</h1>
        </div>
      </div>

      <div className="stats">
        {statCards.map((card) => (
          <div className="stat" key={card.label}>
            <div className="stat__label">
              <span className={`stat__dot stat__dot--${card.dot}`} />
              {card.label}
            </div>

            {isStatsLoading || card.value === null ? (
              <div className="skeleton stat__skeleton" />
            ) : (
              <div className={card.money ? "stat__value stat__value--money" : "stat__value"}>
                {card.value}
              </div>
            )}

            {card.hint && <div className="stat__hint">{card.hint}</div>}
          </div>
        ))}
      </div>

      {actionError && (
        <div className="banner banner--err" style={{ marginBottom: 0 }}>
          <span className="banner__icon">
            <AlertIcon size={15} />
          </span>
          <span>{actionError}</span>
        </div>
      )}

      <FiltersPanel
        draftValues={draftValues}
        appliedFilters={appliedFilters}
        hasActiveFilters={hasActiveFilters}
        onDraftChange={setDraft}
        onApplyText={applyTextFilter}
        onApplyAnswer={applyAnswerFilter}
        onRemove={removeFilter}
        onClear={clearFilters}
      />

      <div className="card">
        <Toolbar
          filters={apiFilters}
          isLoading={isLoading}
          pendingCount={stats?.pendingRecords ?? 0}
          hasRecords={(pagination?.totalRecords ?? 0) > 0}
          onUploadClick={() => {
            setActionError(null);
            setIsUploadOpen(true);
          }}
          onSendClick={() => {
            setActionError(null);
            setIsSendOpen(true);
          }}
          onRefresh={refresh}
          onError={setActionError}
        />

        <BenefitsTable
          benefits={benefits}
          isLoading={isLoading}
          error={error}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />

        <Pagination
          pagination={pagination}
          limit={limit}
          isLoading={isLoading}
          onPageChange={setPage}
          onLimitChange={handleLimitChange}
        />
      </div>

      {isUploadOpen && (
        <UploadModal
          user={user}
          onClose={() => setIsUploadOpen(false)}
          onUploaded={refresh}
        />
      )}

      {isSendOpen && (
        <SendModal
          pendingCount={stats?.pendingRecords ?? 0}
          onClose={() => setIsSendOpen(false)}
          onSent={refresh}
        />
      )}
    </>
  );
}
